import type { WeightTotalResult } from './types.js';

/** Weights within one entry must sum to exactly this many basis points. */
export const TOTAL_BP = 10_000;

/**
 * The smallest share a destination may hold.
 *
 * Zero, deliberately. A destination at 0% is a parked one: it stays configured,
 * keeps its id and therefore its traffic history, and is simply never chosen.
 * Refusing to save it forced the operator to delete a destination in order to
 * take it out of rotation, which threw away the history along with the row —
 * and made "add a destination now, weight it later" impossible.
 */
export const MIN_BP = 0;

export function percentToBp(percent: number): number {
  return Math.round(percent * 100);
}

export function bpToPercent(bp: number): number {
  return bp / 100;
}

export function sumBp(values: readonly number[]): number {
  let total = 0;
  for (const value of values) total += value;
  return total;
}

export function validateWeightTotal(values: readonly number[]): WeightTotalResult {
  const totalBp = sumBp(values);
  if (totalBp === TOTAL_BP) return { valid: true };
  return { valid: false, totalBp, deltaBp: totalBp - TOTAL_BP };
}

/** Even split, with the remainder given to the earliest entries so the total is exact. */
export function distributeEvenly(count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(TOTAL_BP / count);
  const remainder = TOTAL_BP - base * count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

/**
 * Spreads the unallocated share evenly across the rows that are not locked.
 *
 * This replaced a `rebalance` that fired on every keystroke, silently moving
 * every other row to keep the total at 100%. It made the editor unusable in
 * the way that matters: setting the second weight moved the first one you had
 * just set, so there was no order in which three values could be typed. Now a
 * typed weight is the weight, the editor reports what is left to allocate, and
 * this is the one explicit action that fills the gap.
 *
 * Locked rows are kept exactly as they are — a lock is the operator saying
 * "this number is decided", and an even distribution that overrode it would
 * make the lock decorative.
 */
export function distributeRemainder(
  weightsBp: readonly number[],
  lockedIndices: readonly number[] = [],
): number[] {
  const locked = new Set(lockedIndices);
  const unlocked = weightsBp.map((_, index) => index).filter((index) => !locked.has(index));
  if (unlocked.length === 0) return [...weightsBp];

  const lockedTotal = sumBp(weightsBp.filter((_, index) => locked.has(index)));
  const remaining = Math.max(0, TOTAL_BP - lockedTotal);
  const base = Math.floor(remaining / unlocked.length);
  const spare = remaining - base * unlocked.length;

  const result = [...weightsBp];
  unlocked.forEach((index, position) => {
    result[index] = base + (position < spare ? 1 : 0);
  });
  return result;
}
